import {
  PosDevice,
  PosSessionEvent,
  PosSessionEventsPage,
  colors,
} from '@bofrak-backend/shared';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Center,
  Heading,
  HStack,
  Icon,
  Skeleton,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from '@chakra-ui/react';
import { formatDate, formatRelativeToNow } from '@bofrak-backend/shared-i18n';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FaArrowRight,
  FaCheckCircle,
  FaExclamationTriangle,
  FaMapMarkerAlt,
  FaPowerOff,
  FaSignInAlt,
  FaSignOutAlt,
} from 'react-icons/fa';
import { FiArrowLeft } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useRecoilValue } from 'recoil';
import { apiAdapter } from '../api/backend';
import Main from '../components/Main';
import { merchantAtom, storeAtom } from '../recoil/atoms';
import { tShared, useSharedUiTranslation } from '../../i18n';

// Leaflet
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';

// React-Leaflet 4 + Leaflet 1.9 doesn't ship icon URLs that work with
// bundlers out of the box; wire them up to the CDN copies so markers
// render correctly without copying assets into every consumer app.
const defaultMarkerIcon = L.icon({
  iconUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  iconRetinaUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  shadowUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

type Status = 'Online' | 'Idle' | 'Offline';
const ONLINE_THRESHOLD_MS = 120_000;

function deviceStatus(device: PosDevice): Status {
  if (!device.current_user_id) return 'Offline';
  const seen = device.current_user_last_seen_at;
  if (!seen) return 'Idle';
  const ageMs = Date.now() - new Date(seen).getTime();
  return ageMs <= ONLINE_THRESHOLD_MS ? 'Online' : 'Idle';
}

function StatusBadge({ status }: { status: Status }) {
  const { t } = useSharedUiTranslation();
  const scheme =
    status === 'Online' ? 'green' : status === 'Idle' ? 'yellow' : 'gray';
  const label =
    status === 'Online'
      ? t('Online')
      : status === 'Idle'
        ? t('Idle')
        : t('Offline');
  return (
    <Badge colorScheme={scheme} fontSize="2xs" px={1.5} borderRadius="sm">
      {label}
    </Badge>
  );
}

function eventVisuals(eventType: PosSessionEvent['event_type']) {
  switch (eventType) {
    case 'CLAIM':
      return {
        icon: FaSignInAlt,
        color: 'green.600',
        title: tShared('Signed in'),
      };
    case 'EVICT':
      return {
        icon: FaSignOutAlt,
        color: 'orange.600',
        title: tShared('Signed out'),
      };
    case 'HEARTBEAT_FAIL':
      return {
        icon: FaExclamationTriangle,
        color: 'red.600',
        title: tShared('Session lost'),
      };
    // Made on the till while the backend was unreachable, uploaded later.
    case 'OFFLINE_SIGN_IN':
      return {
        icon: FaSignInAlt,
        color: 'green.600',
        title: `${tShared('Signed in')} (${tShared('Offline')})`,
      };
    case 'OFFLINE_SIGN_OUT':
      return {
        icon: FaSignOutAlt,
        color: 'orange.600',
        title: `${tShared('Signed out')} (${tShared('Offline')})`,
      };
    default:
      return { icon: FaPowerOff, color: 'gray.500', title: eventType };
  }
}

function formatLocationSource(src?: string) {
  if (!src) return undefined;
  if (src === 'GPS') return 'GPS';
  if (src === 'IP') return 'IP';
  return src;
}

function EventCard({ event }: { event: PosSessionEvent }) {
  const { t } = useSharedUiTranslation();
  const v = eventVisuals(event.event_type);
  const hasLatLng =
    event.latitude !== undefined &&
    event.latitude !== null &&
    event.longitude !== undefined &&
    event.longitude !== null;
  const mapsHref = hasLatLng
    ? `https://maps.google.com/?q=${event.latitude},${event.longitude}`
    : undefined;
  return (
    <Box
      p={3}
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="md"
      bg="white"
      w="full">
      <HStack align="start" spacing={3}>
        <Icon as={v.icon} color={v.color} boxSize={4} mt={1} />
        <VStack align="start" spacing={1} flex={1}>
          <Text fontSize="sm" fontWeight="medium">
            {v.title}
            {event.device_label ? ` — ${event.device_label}` : ''}
          </Text>
          <Text fontSize="xs" color="gray.500">
            {formatDate(new Date(event.occurred_at), 'dd MMM yyyy, HH:mm')}
          </Text>
          {hasLatLng && (
            <HStack spacing={1}>
              <Icon as={FaMapMarkerAlt} boxSize={3} color="gray.400" />
              <Text fontSize="xs" color="gray.600">
                {Number(event.latitude).toFixed(5)},{' '}
                {Number(event.longitude).toFixed(5)}
                {event.accuracy_meters
                  ? ` ±${Math.round(event.accuracy_meters)}m`
                  : ''}
                {formatLocationSource(event.location_source)
                  ? ` · ${formatLocationSource(event.location_source)}`
                  : ''}
              </Text>
              {mapsHref && (
                <Button
                  as="a"
                  href={mapsHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="link"
                  size="xs"
                  colorScheme="blue">
                  {t('View on map')}
                </Button>
              )}
            </HStack>
          )}
          {event.evicted_by_device_label && event.event_type !== 'CLAIM' && (
            <Text fontSize="xs" color="gray.600">
              {t('By: {{device}}', { device: event.evicted_by_device_label })}
            </Text>
          )}
          {event.reason && (
            <Text fontSize="xs" color="gray.500">
              {t('Reason: {{reason}}', { reason: event.reason })}
            </Text>
          )}
        </VStack>
      </HStack>
    </Box>
  );
}

function DeviceHistoryPanel({
  device,
  expanded,
}: {
  device: PosDevice;
  expanded: boolean;
}) {
  const { t } = useSharedUiTranslation();
  const [pages, setPages] = useState<PosSessionEventsPage[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);

  const initialQuery = useQuery(
    ['pos-device-events', device.id],
    () =>
      apiAdapter.listSessionEventsByDevice({
        pos_device_id: device.id,
        limit: 50,
      }),
    {
      enabled: expanded,
      onSuccess: (page) => setPages([page]),
    },
  );

  const events = useMemo(() => pages.flatMap((p) => p.events), [pages]);
  const lastCursor = pages.length ? pages[pages.length - 1].cursor : undefined;

  const loadMore = useCallback(async () => {
    if (!lastCursor) return;
    setLoadingMore(true);
    try {
      const next = await apiAdapter.listSessionEventsByDevice({
        pos_device_id: device.id,
        cursor: lastCursor,
        limit: 50,
      });
      setPages((prev) => [...prev, next]);
    } finally {
      setLoadingMore(false);
    }
  }, [device.id, lastCursor]);

  if (initialQuery.isLoading) {
    return (
      <VStack spacing={2} align="stretch">
        <Skeleton h="60px" />
        <Skeleton h="60px" />
        <Skeleton h="60px" />
      </VStack>
    );
  }

  if (initialQuery.isError) {
    return (
      <Text fontSize="sm" color="red.600">
        {t('Failed to load session history.')}
      </Text>
    );
  }

  if (events.length === 0) {
    return (
      <Text fontSize="sm" color="gray.500">
        {t('No session events recorded for this device yet.')}
      </Text>
    );
  }

  return (
    <VStack spacing={2} align="stretch">
      {events.map((e) => (
        <EventCard key={e.id} event={e} />
      ))}
      {lastCursor && (
        <Center>
          <Button
            size="sm"
            variant="outline"
            onClick={loadMore}
            isLoading={loadingMore}>
            {t('Load more')}
          </Button>
        </Center>
      )}
    </VStack>
  );
}

function FitBounds({ points }: { points: [number, number][] }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) {
      map.setView(points[0], 15);
      return;
    }
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [40, 40] });
  }, [map, points]);
  return null;
}

function PosDeviceActivity() {
  const { t } = useSharedUiTranslation();
  const navigate = useNavigate();
  const merchant = useRecoilValue(merchantAtom);
  const store = useRecoilValue(storeAtom);

  const [tabIndex, setTabIndex] = useState(0);
  const [accordionIndex, setAccordionIndex] = useState<number[]>([]);

  const devicesQuery = useQuery(
    ['pos-devices', merchant?.id, store?.id],
    async () => {
      if (!merchant || !store) return [] as PosDevice[];
      const all: PosDevice[] = [];
      let cursor: string | undefined = undefined;
      do {
        const page = await apiAdapter.getPOSDevices({
          merchant_id: merchant.id,
          store_id: store.id,
          limit: 100,
          cursor,
        });
        all.push(...page.pos_devices);
        cursor = page.cursor;
      } while (cursor);
      return all;
    },
    { enabled: !!merchant && !!store },
  );

  const devices = devicesQuery.data ?? [];

  // Sort: Online first, then Idle, then Offline; tiebreak on name/prefix.
  const sortedDevices = useMemo(() => {
    const rank: Record<Status, number> = { Online: 0, Idle: 1, Offline: 2 };
    return [...devices].sort((a, b) => {
      const r = rank[deviceStatus(a)] - rank[deviceStatus(b)];
      if (r !== 0) return r;
      return (a.prefix || a.name).localeCompare(b.prefix || b.name);
    });
  }, [devices]);

  const mapPoints = useMemo<[number, number][]>(
    () =>
      sortedDevices
        .filter(
          (d) =>
            typeof d.current_user_latitude === 'number' &&
            typeof d.current_user_longitude === 'number',
        )
        .map((d) => [
          d.current_user_latitude as number,
          d.current_user_longitude as number,
        ]),
    [sortedDevices],
  );

  const handleOpenHistoryFor = (deviceId: string) => {
    const idx = sortedDevices.findIndex((d) => d.id === deviceId);
    if (idx < 0) return;
    setTabIndex(0);
    setAccordionIndex((prev) => (prev.includes(idx) ? prev : [...prev, idx]));
  };

  if (!merchant || !store) {
    return (
      <Main>
        <Center py={10}>
          <Text color="gray.500">
            {t('Select a store to view POS activity.')}
          </Text>
        </Center>
      </Main>
    );
  }

  return (
    <Main>
      <VStack spacing={4} w="full" align="stretch" pb={4}>
        <Card>
          <CardHeader pb={2}>
            <HStack w="full">
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<Icon as={FiArrowLeft} />}
                onClick={() => navigate(-1)}
                px={2}>
                {t('Back')}
              </Button>
              <Heading size="md" flex={1} textAlign="center" pr={10}>
                {t('POS Device Activity')}
              </Heading>
            </HStack>
          </CardHeader>
          <CardBody pt={2}>
            <Tabs
              index={tabIndex}
              onChange={setTabIndex}
              colorScheme="blue"
              variant="line"
              isLazy>
              <TabList>
                <Tab fontSize="sm">{t('History')}</Tab>
                <Tab fontSize="sm">{t('Map')}</Tab>
              </TabList>
              <TabPanels>
                <TabPanel px={0}>
                  {devicesQuery.isLoading ? (
                    <Center py={6}>
                      <Spinner />
                    </Center>
                  ) : sortedDevices.length === 0 ? (
                    <Text fontSize="sm" color="gray.500" py={4}>
                      {t('No POS devices configured for this store yet.')}
                    </Text>
                  ) : (
                    <Accordion
                      allowMultiple
                      index={accordionIndex}
                      onChange={(i) =>
                        setAccordionIndex(
                          Array.isArray(i) ? i : i === -1 ? [] : [i],
                        )
                      }>
                      {sortedDevices.map((device, idx) => {
                        const status = deviceStatus(device);
                        const expanded = accordionIndex.includes(idx);
                        return (
                          <AccordionItem key={device.id}>
                            <AccordionButton>
                              <HStack flex={1} spacing={3} textAlign="left">
                                <Text fontSize="sm" fontWeight="medium">
                                  {device.prefix} — {device.name}
                                </Text>
                                <StatusBadge status={status} />
                                {device.current_user_device_label && (
                                  <Text
                                    fontSize="xs"
                                    color="gray.500"
                                    noOfLines={1}>
                                    {device.current_user_device_label}
                                  </Text>
                                )}
                              </HStack>
                              <AccordionIcon />
                            </AccordionButton>
                            <AccordionPanel pb={4}>
                              <DeviceHistoryPanel
                                device={device}
                                expanded={expanded}
                              />
                            </AccordionPanel>
                          </AccordionItem>
                        );
                      })}
                    </Accordion>
                  )}
                </TabPanel>
                <TabPanel px={0}>
                  {devicesQuery.isLoading ? (
                    <Center py={6}>
                      <Spinner />
                    </Center>
                  ) : mapPoints.length === 0 ? (
                    <Text fontSize="sm" color="gray.500" py={4}>
                      {t(
                        'No active POS sessions with known locations to show.',
                      )}
                    </Text>
                  ) : (
                    <Box h="500px" borderRadius="md" overflow="hidden">
                      <MapContainer
                        center={mapPoints[0]}
                        zoom={13}
                        scrollWheelZoom
                        style={{ width: '100%', height: '100%' }}>
                        <TileLayer
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <FitBounds points={mapPoints} />
                        {sortedDevices
                          .filter(
                            (d) =>
                              typeof d.current_user_latitude === 'number' &&
                              typeof d.current_user_longitude === 'number',
                          )
                          .map((d) => {
                            const claimedAt = d.current_user_claimed_at
                              ? formatRelativeToNow(
                                  new Date(d.current_user_claimed_at),
                                  { addSuffix: true },
                                )
                              : '—';
                            const lastSeen = d.current_user_last_seen_at
                              ? formatRelativeToNow(
                                  new Date(d.current_user_last_seen_at),
                                  { addSuffix: true },
                                )
                              : '—';
                            const status = deviceStatus(d);
                            return (
                              <Marker
                                key={d.id}
                                icon={defaultMarkerIcon}
                                position={[
                                  d.current_user_latitude as number,
                                  d.current_user_longitude as number,
                                ]}>
                                <Popup>
                                  <VStack
                                    align="start"
                                    spacing={1}
                                    minW="200px">
                                    <HStack spacing={2}>
                                      <Text
                                        fontSize="sm"
                                        fontWeight="bold"
                                        color={colors.black}>
                                        {d.prefix} — {d.name}
                                      </Text>
                                      <StatusBadge status={status} />
                                    </HStack>
                                    {d.current_user_device_label && (
                                      <Text fontSize="xs" color="gray.700">
                                        {d.current_user_device_label}
                                      </Text>
                                    )}
                                    <Text fontSize="xs" color="gray.600">
                                      {t('Signed in {{when}}', {
                                        when: claimedAt,
                                      })}
                                    </Text>
                                    <Text fontSize="xs" color="gray.600">
                                      {t('Last seen {{when}}', {
                                        when: lastSeen,
                                      })}
                                    </Text>
                                    {d.current_user_accuracy_meters && (
                                      <Text fontSize="xs" color="gray.600">
                                        {t('Accuracy ±{{meters}}m', {
                                          meters: Math.round(
                                            d.current_user_accuracy_meters,
                                          ),
                                        })}
                                        {d.current_user_location_source
                                          ? ` · ${formatLocationSource(d.current_user_location_source)}`
                                          : ''}
                                      </Text>
                                    )}
                                    <Button
                                      size="xs"
                                      variant="link"
                                      colorScheme="blue"
                                      rightIcon={
                                        <Icon as={FaArrowRight} boxSize={3} />
                                      }
                                      onClick={() =>
                                        handleOpenHistoryFor(d.id)
                                      }>
                                      {t('View history')}
                                    </Button>
                                  </VStack>
                                </Popup>
                              </Marker>
                            );
                          })}
                      </MapContainer>
                    </Box>
                  )}
                </TabPanel>
              </TabPanels>
            </Tabs>
          </CardBody>
        </Card>
      </VStack>
    </Main>
  );
}

export default PosDeviceActivity;
